import { Component, type ErrorInfo, type ReactNode } from 'react'

interface ErrorBoundaryProps {
  children: ReactNode
  resetKey?: unknown
}

interface ErrorBoundaryState {
  error: Error | null
  chunkReloadGuarded: boolean
}

const CHUNK_RELOAD_WINDOW_MS = 60_000
const CHUNK_RELOAD_PREFIX = 'openvoter:chunk-reload:v1:'

export function isChunkLoadError(error: Error): boolean {
  return /(error loading dynamically imported module|failed to fetch dynamically imported module|importing a module script failed|chunkloaderror|loading chunk .+ failed|failed to fetch module script)/i.test(`${error.name}: ${error.message}`)
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, chunkReloadGuarded: false }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error, chunkReloadGuarded: false }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('UI error caught by boundary:', error, info.componentStack)
    if (!isChunkLoadError(error)) return
    const key = `${CHUNK_RELOAD_PREFIX}${window.location.pathname}${window.location.search}`
    try {
      const prior = Number(window.sessionStorage.getItem(key) || 0)
      if (Date.now() - prior < CHUNK_RELOAD_WINDOW_MS) {
        this.setState({ chunkReloadGuarded: true })
        return
      }
      window.sessionStorage.setItem(key, String(Date.now()))
      window.location.reload()
    } catch {
      this.setState({ chunkReloadGuarded: true })
    }
  }

  componentDidUpdate(prev: ErrorBoundaryProps): void {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, chunkReloadGuarded: false })
    }
  }

  private handleReset = (): void => {
    this.setState({ error: null, chunkReloadGuarded: false })
  }

  private handleReload = (): void => {
    window.location.reload()
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children
    const message = this.state.error.message || 'An unexpected error occurred'
    const chunkError = isChunkLoadError(this.state.error)
    // Raw technical details (library error names, stack paths, etc.) live
    // inside a default-closed <details> disclosure. Sighted keyboard users
    // can opt in; screen readers announce it via the native disclosure
    // semantics so it never intrudes into the user-facing error message.
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-rose-200 bg-rose-50 p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-rose-900">{chunkError ? 'This page couldn’t be updated' : 'Something went wrong'}</h2>
        <p className="mt-2 text-sm text-rose-800">
          {chunkError
            ? 'A required app file could not be loaded. Reload the page to try again.'
            : "This section of the app hit an error and couldn't render. You can try returning to this view, or reload the page if it keeps happening."}
        </p>
        <details className="mt-3 rounded-lg bg-rose-100 px-3 py-2">
          <summary className="cursor-pointer text-xs font-semibold text-rose-900 hover:text-rose-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500">
            Show technical details
          </summary>
          <pre className="mt-2 overflow-x-auto rounded bg-rose-50 p-2 text-xs text-rose-900">
            {message}
          </pre>
        </details>
        <div className="mt-4 flex gap-2">
          {!chunkError && <button
            type="button"
            onClick={this.handleReset}
            className="rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
          >
            Try again
          </button>}
          <button
            type="button"
            onClick={this.handleReload}
            className="rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2 focus-visible:ring-offset-rose-50"
          >
            Reload page
          </button>
        </div>
      </div>
    )
  }
}
